import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import './Antrag.css';
import { FaArrowLeft } from "react-icons/fa";
import { IoIosAdd, IoIosRemove } from "react-icons/io";
import { IoIosCard } from "react-icons/io";
import { IoSend } from "react-icons/io5";
import { FaCloudDownloadAlt } from "react-icons/fa";
import { TiArrowBack } from "react-icons/ti";
import PersonalDataForm from './PersonalDataForm';
import EducationDataForm from './EducationDataForm';
import WorkExperienceDataForm from './WorkExperienceDataForm';
import DocumentForm from './DocumentForm';
import FileUpload from './FileUpload';
import axios from 'axios';
import { API_BASE_URL } from '../../constants';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../AuthContext';
import AntragButtons from './AntragButtons';
import { IDataItem } from '../Models/IDataItem';
import { DOCUMENT_TYPES } from '../../constants/docTypes';

const Antrag: React.FC = () => {
    const [activeSection, setActiveSection] = useState<string>('Antrag starten');
    const { user } = useAuth();
    const location = useLocation();
    const [isViewOnly, setIsViewOnly] = useState(false);

    const [saveAttempted, setSaveAttempted] = useState({
        start: false,
        personal: false,
        education: false,
        work: false,
        documents: false,
    });

    // Scroll behavior for mobile devices
    const scrollToPosition = () => {
        const isMobile = window.innerWidth <= 768;
        const topValue = isMobile ? 460 : 100;

        window.scrollTo({ top: topValue, behavior: 'smooth' });
    };

    // New Application Validation
    const [applicationValidationData, setApplicationValidationData] = useState<{
        bachDegree: string | null;
        educationCountry: string | null;
        activeApplication: boolean | null;
        withdrawnApplication: boolean | null;
        educationUse: string | null;
        hasJobRecognition: boolean | null;
        proxy: boolean | null;
        hasJobExperience: boolean | null;
        workInLowerAustria: boolean | null;
        plannedWorkInLowerAustria: boolean | null;
        livesInLowerAustria: boolean | null;
    }>({
        bachDegree: null,
        educationCountry: null,
        activeApplication: null,
        withdrawnApplication: null,
        educationUse: null,
        hasJobRecognition: null,
        proxy: null,
        hasJobExperience: null,
        workInLowerAustria: null,
        plannedWorkInLowerAustria: null,
        livesInLowerAustria: null
    });

    // Personal data
    const [personalData, setPersonalData] = useState({
        firstName: null as string | null,
        lastName: null as string | null,
        email: null as string | null,
        gender: null as string | null,
        phone: null as string | null,
        citizenship: null as string | null,
        birthDate: null as string | null,
        birthPlace: null as string | null,
        street: null as string | null,
        zip: null as string | null,
        city: null as string | null,
        country: null as string | null,
        familyStatus: null as string | null,
        formerName: null as string | null,
    });

    // Education data

    const [schoolData, setSchoolData] = useState({
        schoolName: null as string | null,
        schoolStreet: null as string | null,
        schoolStreetNumber: null as string | null,
        schoolZip: null as string | null,
        schoolCity: null as string | null,
        schoolCountry: null as string | null
    });

    type UniversityData = {
        universityName: string | null;
        universityStreet: string | null;
        universityStreetNumber: string | null;
        universityZip: string | null;
        universityCity: string | null;
        universityCountry: string | null;
        fieldOfStudy: string | null;
        academicDegree: string | null;
        freshmanYear: string | null;
        finalYear: string | null;
        regularStudyTime: string | null;
        studyTimeReason: string | null;
        hasThesis: boolean | null;
    };

    const [universityData, setUniversityData] = useState<UniversityData[]>([{
        universityName: null,
        universityStreet: null,
        universityStreetNumber: null,
        universityZip: null,
        universityCity: null,
        universityCountry: null,
        fieldOfStudy: null,
        academicDegree: null,
        freshmanYear: null,
        finalYear: null,
        regularStudyTime: null,
        studyTimeReason: null,
        hasThesis: null
    }]);

    // Work experience data

    const [workValidationData, setWorkValidationData] = useState<{
        hasExam: boolean | null;
        hasPassedExam?: boolean | null;
        hasNoExamReason?: string | null;
        hasWorkExperience: boolean | null;
    }>({
        hasExam: null,
        hasPassedExam: null,
        hasNoExamReason: null,
        hasWorkExperience: null
    });

    type WorkExperienceData = {
        startDate: string | null;
        endDate: string | null;
        companyName: string | null;
        country: string | null;
        city: string | null;
        description: string | null
    };

    const [workExperienceData, setWorkExperienceData] = useState<WorkExperienceData[]>([{
        startDate: null,
        endDate: null,
        companyName: null,
        country: null,
        city: null,
        description: null
    }]);

    type DocumentItem = {
        name: string;
        description: string;
        guid: string;
    };

    const [documentData, setDocumentData] = useState<DocumentItem[]>([]);

    const [canSubmit, setCanSubmit] = useState(false);

    const canDirectlySubmit = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/canSubmit`, {
                headers: {
                    Authorization: `Bearer ${sessionStorage.getItem('token')}`
                }
            });
            setCanSubmit(response.data);
        } catch (error) {
            console.error('Error fetching canSubmit status:', error);
        }
    };

    useEffect(() => {
        canDirectlySubmit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSection === 'Abschluss']);

    useEffect(() => {
        if (location.state?.viewAntrag) {
            return;
        }

        const user = sessionStorage.getItem('user');

        const getApplicant = async (email: string) => {
            try {
                const response = await axios.post(`${API_BASE_URL}/getuser`,
                    email,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${sessionStorage.getItem('token')}`
                        }
                    }
                );
                const data = response.data;
                setPersonalData(prevState => ({
                    ...prevState,
                    firstName: data.vorname,
                    lastName: data.nachname,
                }));
            } catch (error) {
                console.error('Error retrieving user data:', error);
            }
        };

        if (applicationValidationData.proxy === false) {
            if (user) {
                try {
                    const userObject = JSON.parse(user);
                    if (userObject.role === 'NostrifyUser') {
                        setPersonalData(prevState => ({
                            ...prevState,
                            email: userObject.email
                        }));
                        getApplicant(userObject.email);
                    }
                } catch (error) {
                    console.error('Error parsing user data from sessionStorage:', error);
                }
            }
        }
    }, [applicationValidationData.proxy, location.state?.viewAntrag]);

    // EU + EWR + CH countries
    const [filteredCountries, setFilteredCountries] = useState<string[]>([]);

    // get countries from API
    useEffect(() => {
        const getNations = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/general/filtered-nations`, {
                    headers: {
                        Authorization: `Bearer ${sessionStorage.getItem('token')}`
                    }
                });
                let data: string[] = response.data;

                const updatedData = data
                    .map((country) => country === "Republik Österreich" ? "Österreich" : country) // Namensänderung für Validation
                    .filter((country) => country !== "Staatenlos");

                setFilteredCountries(updatedData);
            } catch (error) {
                console.error("Error fetching nations:", error);
            }
        };
        getNations();
    }, []);

    // #region ValidationData

    const isApplicationValidationDataValid = () => {
        // 1. proxy can't be null, and if true, vollmacht must have values
        const isProxyValid = applicationValidationData.proxy !== null &&
            (applicationValidationData.proxy === false ||
                (applicationValidationData.proxy === true &&
                    documentData.some(doc => doc.description === DOCUMENT_TYPES.VOLLMACHT)));

        // 2 & 3. educationCountry can't be null and can't be in filteredCountries
        const isEducationCountryValid = applicationValidationData.educationCountry !== null &&
            (applicationValidationData.bachDegree === "Musiktherapie" ||
                !filteredCountries.includes(applicationValidationData.educationCountry));

        // 7. Check job recognition and experience requirements
        const isJobRecognitionValid = applicationValidationData.hasJobRecognition !== null &&
            (
                applicationValidationData.hasJobRecognition === false ||
                (
                    applicationValidationData.hasJobRecognition === true &&
                    (
                        applicationValidationData.bachDegree === "Musiktherapie"
                            ? applicationValidationData.hasJobExperience !== null
                            : applicationValidationData.hasJobExperience === false
                    )
                )
            );

        // Check all required fields have values
        return (
            // Basic field checks
            applicationValidationData.proxy !== null &&
            applicationValidationData.bachDegree !== null &&
            applicationValidationData.educationCountry !== null &&
            applicationValidationData.educationUse !== null &&
            applicationValidationData.hasJobRecognition !== null &&
            applicationValidationData.workInLowerAustria !== null &&
            applicationValidationData.plannedWorkInLowerAustria !== null &&
            applicationValidationData.livesInLowerAustria !== null &&

            // Complex validation rules
            isProxyValid &&
            isEducationCountryValid &&
            isJobRecognitionValid &&

            // 4 & 5. Application status requirements
            applicationValidationData.activeApplication === false &&
            applicationValidationData.withdrawnApplication === false
        );
    };

    const handleApplicationValidationDataChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setApplicationValidationData(prevState => ({
            ...prevState,
            [name]: value
        }));
    }

    const handleApplicationCheckboxChange = (name: keyof typeof applicationValidationData, checkboxValue: boolean) => {
        const newValue = applicationValidationData[name] === checkboxValue ? null : checkboxValue;
        setApplicationValidationData(prevState => ({
            ...prevState,
            [name]: newValue
        }));
    };

    const handleEducationUse = (option: string) => {
        setApplicationValidationData(prevState => ({
            ...prevState,
            educationUse: prevState.educationUse === option ? null : option
        }));
    };

    useEffect(() => {
        if (user?.role === "AgencyUser") {
            setApplicationValidationData(prev => ({
                ...prev,
                proxy: true
            }));
        }
    }, [user]);

    // #endregion

    // #region PersonalData
    const isPersonalDataValid = () => {
        if (!personalData || typeof personalData !== 'object') return false;
        const { formerName, ...rest } = personalData;
        return Object.values(rest).every(value => value !== null && value !== '');
    }

    const handlePersonalDataChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setPersonalData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    // #endregion

    // #region EducationData

    const handleSchoolDataChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setSchoolData(prevState => ({
            ...prevState,
            [name]: value
        }));
    }

    const handleEducationDataChange = (index: number, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        const newValue = value === '' ? null : value;
        setUniversityData(prevState => {
            const newData = [...prevState];
            newData[index] = {
                ...newData[index],
                [name]: newValue
            };
            return newData;
        });
    };

    const handleThesisChange = (index: number, value: boolean) => {
        setUniversityData(prevState => {
            const newData = [...prevState];
            newData[index] = {
                ...newData[index],
                hasThesis: newData[index].hasThesis === value ? null : value
            };
            return newData;
        });
    };

    const addEducationData = () => {
        if (universityData.length < 4) {
            setUniversityData(prevState => [
                ...prevState,
                {
                    universityName: null,
                    universityStreet: null,
                    universityStreetNumber: null,
                    universityZip: null,
                    universityCity: null,
                    universityCountry: null,
                    fieldOfStudy: null,
                    academicDegree: null,
                    freshmanYear: null,
                    finalYear: null,
                    regularStudyTime: null,
                    studyTimeReason: null,
                    hasThesis: null
                }
            ]);
        } else {
            toast.warn('Maximale Anzahl an Studienabschlüssen erreicht');
        }
    };

    const removeEducationData = () => {
        setUniversityData(prevState => prevState.slice(0, -1));
    };

    const isEducationDataValid = () => {
        // Check if school data is not null
        const isSchoolDataValid = Object.values(schoolData).every(value => value !== null && value !== '');
        if (!isSchoolDataValid) {
            return false;
        }

        // Check if every element of educationData array is valid
        const isEducationArrayValid = universityData.every(edu => {
            const { finalYear, freshmanYear, regularStudyTime, studyTimeReason, academicDegree, ...rest } = edu;

            // Check if freshmanYear, finalYear, and regularStudyTime are not empty
            if (freshmanYear === null || finalYear === null || regularStudyTime === null) {
                return false;
            }

            // Check if all other fields are not null, except academicDegree
            const isRestValid = Object.entries(rest).every(([key, value]) => {
                if (key === 'academicDegree' || key === 'id') {
                    return true; // Skip validation for academicDegree
                }
                if (typeof value === 'string') {
                    return value !== null;
                }
                if (typeof value === 'boolean') {
                    return true; // Booleans are always valid
                }
                return false; // Invalid type
            });

            if (!isRestValid) {
                return false;
            }

            // Check if studyTimeReason can be null
            if ((Number(finalYear) - Number(freshmanYear)) < Number(regularStudyTime)) {
                return studyTimeReason === null;
            }

            return true;
        });

        return isEducationArrayValid;
    };
    // #endregion

    // #region WorkData
    const isWorkValidationDataValid = () => {
        const filteredWorkValidationData = { ...workValidationData };

        // Exclude `hasPassedExam` if `hasExam` is not "true"
        if (workValidationData.hasExam !== true) {
            delete filteredWorkValidationData.hasPassedExam;
        }

        // Exclude `hasNoExamReason` unless `hasExam` is true and `hasPassedExam` is false
        if (!(workValidationData.hasExam === true && workValidationData.hasPassedExam === false)) {
            delete filteredWorkValidationData.hasNoExamReason;
        }

        // Validate `workExperienceData` only if `hasWorkExperience` is true
        if (workValidationData.hasWorkExperience === true) {
            const isWorkExperienceValid = workExperienceData.every(workExperience =>
                Object.values(workExperience).every(value => value !== null && value !== '')
            );

            if (!isWorkExperienceValid) {
                return false;
            }
        }

        // Check if all required fields in workValidationData are not null
        if (workValidationData.hasExam === null || workValidationData.hasWorkExperience === null) {
            return false;
        }

        if (workValidationData.hasExam === true && workValidationData.hasPassedExam === null) {
            return false;
        }

        return true;
    };

    // Change handlers

    const handleWorkValidationChange = (name: keyof typeof workValidationData, checkboxValue: boolean) => {
        const newValue = workValidationData[name] === checkboxValue ? null : checkboxValue;
        setWorkValidationData(prevState => ({
            ...prevState,
            [name]: newValue
        }));
    };

    const handleWorkExperienceChange = (index: number, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setWorkExperienceData(prevState => {
            const newData = [...prevState];
            newData[index] = {
                ...newData[index],
                [name]: value
            };
            return newData;
        });
    };

    const addWorkExperience = () => {
        if (workExperienceData.length < 8) {
            setWorkExperienceData(prevState => [
                ...prevState,
                {
                    startDate: null,
                    endDate: null,
                    companyName: null,
                    country: null,
                    city: null,
                    description: null
                }
            ]);
        } else {
            toast.warning('Maximale Anzahl an Berufserfahrungen erreicht');
        }
    };

    const removeWorkExperience = () => {
        setWorkExperienceData(prevState => prevState.slice(0, -1));
    };

    // #endregion

    // #region DocumentData
    /*
        const MAX_FILES = 5;

        const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const { name: description, files } = e.target;

            if (!files || files.length === 0) return;

            const newFiles = Array.from(files);
            const existingDocs = documentData.filter(doc => doc.description === description);

            if (existingDocs.length + newFiles.length > MAX_FILES) {
                toast.error(`Sie können maximal ${MAX_FILES} Dateien hochladen.`);
                return;
            }

            const newDocuments = newFiles.map(file => ({
                name: file.name,
                description: description,
                guid: '' // Empty for new uploads
            }));

            setDocumentData(prev => [...prev, ...newDocuments]);
        };
        */

    const handleDocumentUploadSuccess = (description: string, fileName: string, guid: string) => {
        setDocumentData(prev => {
            const newData = [...prev, {
                name: fileName,
                description: description,
                guid: guid
            }];

            return newData;
        });
    };

    const handleDeleteFile = (description: string, fileName: string) => {
        setDocumentData(prev =>
            prev.filter(doc => !(doc.description === description && doc.name === fileName))
        );
    };

    // Helper to get documents by description
    const getDocumentsByDescription = (description: string) => {
        return documentData.filter(doc => doc.description === description);
    };

    // Simplified validation
    const isDocumentDataValid = () => {
        const hasDocuments = (description: string) =>
            documentData.some(doc => doc.description === description);

        const baseValidation =
            hasDocuments(DOCUMENT_TYPES.REISEPASS) &&
            hasDocuments(DOCUMENT_TYPES.LEBENSLAUF) &&
            hasDocuments(DOCUMENT_TYPES.GEBURTSURKUNDE) &&
            hasDocuments(DOCUMENT_TYPES.PRAKTIKUM) &&
            hasDocuments(DOCUMENT_TYPES.TRANSCRIPT) &&
            hasDocuments(DOCUMENT_TYPES.MATURA) &&
            hasDocuments(DOCUMENT_TYPES.DIPLOM);

        if ((personalData.familyStatus === 'verheiratet' || personalData.familyStatus === 'geschieden')
            && !hasDocuments(DOCUMENT_TYPES.HEIRATSURKUNDE)) {
            return false;
        }

        if (universityData.some(edu => edu.hasThesis) && !hasDocuments(DOCUMENT_TYPES.THESIS)) {
            return false;
        }

        if (workValidationData.hasWorkExperience && !hasDocuments(DOCUMENT_TYPES.ARBEITSBESTAETIGUNG)) {
            return false;
        }

        if (workValidationData.hasExam && !hasDocuments(DOCUMENT_TYPES.REGISTRATION)) {
            return false;
        }

        return baseValidation;
    };
    // #endregion

    // #region OtherDocumentData

    type OtherDocumentItem = {
        description: string;
        name: string;
        guid: string;
    };

    const [otherDocumentData, setOtherDocumentData] = useState<OtherDocumentItem[]>([
        {
            description: '',
            name: '',
            guid: ''
        }
    ]);

    // Update the handlers (around line 700)
    const handleOtherDocumentDataChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setOtherDocumentData(prevState => {
            const newData = [...prevState];
            newData[index] = {
                ...newData[index],
                [name]: value
            };
            return newData;
        });
    };

    const handleOtherDocumentUploadSuccess = (index: number, fileName: string, guid: string) => {
        setOtherDocumentData(prevState => {
            const newData = [...prevState];
            newData[index] = {
                ...newData[index],
                name: fileName,
                guid: guid
            };
            return newData;
        });

        // Also add to main documentData with the description
        const description = otherDocumentData[index].description;
        handleDocumentUploadSuccess(description, fileName, guid);
    };

    const handleDeleteOther = (index: number) => {
        const doc = otherDocumentData[index];
        if (doc.name && doc.guid) {
            // Delete from main documentData
            handleDeleteFile(doc.description, doc.name);
        }

        // Reset the file fields but keep the description
        setOtherDocumentData(prevState => {
            const newData = [...prevState];
            newData[index] = {
                ...newData[index],
                name: '',
                guid: ''
            };
            return newData;
        });
    };

    const addOtherDocument = () => {
        if (otherDocumentData.length >= 8) {
            toast.warn('Maximale Anzahl an Dokumenten erreicht');
            return;
        }
        setOtherDocumentData(prevState => [
            ...prevState,
            {
                description: '',
                name: '',
                guid: ''
            }
        ]);
    };

    const deleteOtherDocument = () => {
        setOtherDocumentData(prevState => prevState.slice(0, -1));
    };

    // #endregion

    // #region DataProtection & Abschluss

    const [dataProtectionData, setDataProtectionData] = useState({
        dataProcessing: false,
        correctData: false,
        oath: false,
        feeExpires: false
    });

    const toggleSwitch = (section: keyof typeof dataProtectionData) => {
        setDataProtectionData(prevState => ({
            ...prevState,
            [section]: !prevState[section]
        }));
    };

    // #endregion

    useEffect(() => {
        setUniversityData(prevState => {
            const newData = prevState.map((edu, index) => {
                if (edu.finalYear && edu.freshmanYear && edu.regularStudyTime) {
                    if ((Number(edu.finalYear) - Number(edu.freshmanYear)) <= Number(edu.regularStudyTime)) {
                        // Only reset if the value is not already empty to avoid unnecessary updates
                        if (edu.studyTimeReason !== null) {
                            return { ...edu, studyTimeReason: null };
                        }
                    }
                }
                return edu;
            });
            return JSON.stringify(newData) === JSON.stringify(prevState) ? prevState : newData;
        });
        if (!workValidationData.hasWorkExperience) {
            setWorkExperienceData([{
                startDate: null,
                endDate: null,
                companyName: null,
                country: null,
                city: null,
                description: null
            }]);
        }

    }, [universityData, workValidationData.hasWorkExperience]);

    const [nations, setNations] = useState<IDataItem[]>([]);

    const countries = useMemo(() => {
        return nations
            .map((n: any) => n.Value ?? n.value ?? n.Langtext ?? '')
            .filter(Boolean)
            .sort((a: string, b: string) => a.localeCompare(b, 'de', { sensitivity: 'base' }));
    }, [nations]);

    useEffect(() => {
        const fetchNations = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/general/nations`, {
                    headers: {
                        Authorization: `Bearer ${sessionStorage.getItem('token')}`
                    }
                });
                setNations(response.data);
            } catch (error) {
                console.error('Error fetching nations:', error);
            }
        };
        fetchNations();
    }, []);

    const [antragId, setAntragId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false); // Add loading state for saving Antrag
    const [isFetching, setIsFetching] = useState(false); // Add loading state for fetching Antrag

    const latestData = useRef({
        applicationValidationData,
        personalData,
        schoolData,
        universityData,
        workValidationData,
        workExperienceData,
        documentData,
        otherDocumentData,
        dataProtectionData
    });

    // Update the ref whenever the data changes
    useEffect(() => {
        latestData.current = {
            applicationValidationData,
            personalData,
            schoolData,
            universityData,
            workValidationData,
            workExperienceData,
            documentData,
            otherDocumentData,
            dataProtectionData
        };
    }, [applicationValidationData, personalData, schoolData, universityData, workValidationData, workExperienceData, documentData, otherDocumentData, dataProtectionData]);

    const handleSave = async (manual: boolean = false) => {
        const data = latestData.current;
        const userId = user?.id;
        const url = antragId ? `${API_BASE_URL}/application/user/${userId}/antrag/${antragId}` : `${API_BASE_URL}/application/user/${userId}/antrag`;

        try {
            setLoading(true);
            const method = antragId ? 'put' : 'post';
            const response = await (manual ? toast.promise(
                axios({
                    method: method,
                    url: url,
                    data: data,
                    headers: {
                        Authorization: `Bearer ${sessionStorage.getItem('token')}`
                    }
                }),
                {
                    pending: 'Antrag wird gespeichert...',
                    success: 'Antrag erfolgreich gespeichert',
                    error: 'Fehler beim Speichern des Antrags'
                }
            ) : axios({
                method: method,
                url: url,
                data: data,
                headers: {
                    Authorization: `Bearer ${sessionStorage.getItem('token')}`
                }
            }));

            if (method === 'post') {
                setAntragId(response.data);
                sessionStorage.setItem('antragId', response.data.toString());
            }
        } catch (error) {
            console.error('Error saving Antrag:', error);
        } finally {
            setLoading(false);
        }
    };

    // Save Antrag every 30 seconds

    useEffect(() => {
        if (antragId && !location.state?.viewAntrag) {
            const intervalId = setInterval(() => {
                handleSave();
            }, 30000);

            return () => clearInterval(intervalId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [antragId, location.state?.viewAntrag]);

    useEffect(() => {
        if (location.state?.editAntrag) {
            setAntragId(location.state.id);
        }
        if (location.state?.viewAntrag) {
            setAntragId(location.state.id);
            setIsViewOnly(true);
        } else {
            setIsViewOnly(false);
        }
    }, [location.state?.editAntrag, location.state.id, location.state?.viewAntrag]);

    // fetch Antrag for viewing or editing

    useEffect(() => {
        const fetchAntrag = async () => {
            if (!antragId) return;

            setIsFetching(true);
            try {
                const response = await axios.get(`${API_BASE_URL}/application/user/${user?.id}/antrag/${antragId}`, {
                    headers: {
                        Authorization: `Bearer ${sessionStorage.getItem('token')}`
                    }
                });
                if (response.data) {
                    const antrag = response.data;
                    setApplicationValidationData(antrag.applicationValidationData);
                    setPersonalData(antrag.personalData);
                    setSchoolData(antrag.schoolData);
                    setUniversityData(antrag.universityData);
                    setWorkValidationData(antrag.workValidationData);
                    setWorkExperienceData(antrag.workExperienceData);
                    setDocumentData(antrag.documentData || []);
                    setOtherDocumentData(antrag.otherDocumentData);
                    setDataProtectionData(antrag.dataProtectionData);
                }
            } catch (error) {
                console.error('Error fetching Antrag:', error);
            } finally {
                setIsFetching(false);
            }
        };

        if (location.state?.editAntrag || location.state?.viewAntrag) {
            fetchAntrag();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state.editAntrag, antragId, location.state.viewAntrag]);

    // Zahlungsfunktion

    const handlePayment = async () => {
        try {
            const response = await axios.post(`${API_BASE_URL}/checkout-stripe`, {
                antragId: antragId
            }, {
                headers: {
                    Authorization: `Bearer ${sessionStorage.getItem('token')}`
                }
            });

            window.location.href = response.data;

        } catch (error) {
            console.error('Error creating payment:', error);
        }
    };

    const handleAgenySubmit = async () => {
        try {
            await handleSave();
            const response = await axios.post(`${API_BASE_URL}/application/agency-submit`,
                antragId
                , {
                    headers: {
                        Authorization: `Bearer ${sessionStorage.getItem('token')}`,
                        "Content-Type": "application/json"
                    }
                });
            if (response.status === 200)
                window.location.href = '/dashboard'

        } catch (error) {
            console.error('Error creating application:', error);
        }
    }

    return (
        <div className={`ap-container ${isViewOnly ? 'view-only' : ''}`} >
            <div className='usermenu-antrag'>
                <button
                    className={`menu-button ${activeSection === 'Antrag starten' ? 'active-button' : ''}`}
                    type='button'
                    onClick={() => setActiveSection('Antrag starten')}>
                    Neuer Antrag
                </button>
                <button
                    className={`menu-button ${activeSection === 'Persönliche Daten' ? 'active-button' : ''}`}
                    type='button'
                    onClick={() => setActiveSection('Persönliche Daten')}
                    disabled={!isApplicationValidationDataValid()}
                >
                    Persönliche Daten
                </button>
                <button
                    className={`menu-button ${activeSection === 'Ausbildung' ? 'active-button' : ''}`}
                    type='button'
                    onClick={() => setActiveSection('Ausbildung')}
                    disabled={!isApplicationValidationDataValid() || !isPersonalDataValid()}>
                    Ausbildung
                </button>
                <button
                    className={`menu-button ${activeSection === 'Berufserfahrung' ? 'active-button' : ''}`}
                    type='button'
                    onClick={() => setActiveSection('Berufserfahrung')}
                    disabled={!isApplicationValidationDataValid() || !isPersonalDataValid() || !isEducationDataValid()}>
                    Berufsberechtigung und Berufserfahrung
                </button>
                <button
                    className={`menu-button ${activeSection === 'Dokumente' ? 'active-button' : ''}`}
                    type='button'
                    onClick={() => setActiveSection('Dokumente')}
                    disabled={!isApplicationValidationDataValid() || !isPersonalDataValid() || !isEducationDataValid() || !isWorkValidationDataValid()}
                >
                    Dokumente
                </button>
                <button
                    className={`menu-button ${activeSection === 'Abschluss' ? 'active-button' : ''}`}
                    type='button'
                    onClick={() => setActiveSection('Abschluss')}
                    disabled={!isApplicationValidationDataValid() || !isPersonalDataValid() || !isEducationDataValid() || !isWorkValidationDataValid() || !isDocumentDataValid()}
                >
                    Abschluss
                </button>
                {isViewOnly && (
                    <button
                        className='menu-button'
                        type='button'
                        onClick={() => window.history.back()}
                    >
                        <TiArrowBack /> Zurück zum Dashboard
                    </button>
                )}
            </div>
            {isFetching ? (
                <div className='loader' style={{ borderColor: "#9ca4b2" }}></div>
            ) : (
                <div className="antrag-container">
                    {activeSection === 'Antrag starten' && (
                        <div>
                            <h2 className="antrag-heading">Neuer Antrag</h2>
                            <p className='antrag-subheading'>
                                Bitte füllen Sie alle Felder korrekt und wahrheitsgemäß aus, um mit der Bearbeitung Ihres Antrags starten zu können.
                            </p>
                            <div className={`form-element${saveAttempted.start && applicationValidationData.proxy === null ? ' error' : ''}`}>
                                <p>
                                    Reichen Sie den Antrag für eine andere Person ein?
                                </p>
                                <div>
                                    <label className='checkbox-label'>
                                        <input
                                            type="checkbox"
                                            name="proxy"
                                            checked={applicationValidationData.proxy === true}
                                            onChange={() => handleApplicationCheckboxChange("proxy", true)}
                                            disabled={user?.role === "AgencyUser"}
                                        />
                                        Ja
                                        <span
                                            className='checkmark'
                                            tabIndex={(isViewOnly || user?.role === "AgencyUser") ? -1 : 0}
                                            role="checkbox"
                                            aria-checked={applicationValidationData.proxy === true}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleApplicationCheckboxChange("proxy", true);
                                                }
                                            }}
                                        ></span>
                                    </label>
                                    <label className='checkbox-label'>
                                        <input
                                            type="checkbox"
                                            name="proxy"
                                            checked={applicationValidationData.proxy === false}
                                            onChange={() => handleApplicationCheckboxChange("proxy", false)}
                                            disabled={user?.role === "AgencyUser"}
                                        />
                                        Nein
                                        <span
                                            className='checkmark'
                                            tabIndex={(isViewOnly || user?.role === "AgencyUser") ? -1 : 0}
                                            role="checkbox"
                                            aria-checked={applicationValidationData.proxy === false}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleApplicationCheckboxChange("proxy", false);
                                                }
                                            }}
                                        ></span>
                                    </label>
                                </div>
                                {applicationValidationData.proxy === true && (
                                    <>
                                        <a href='Nostrifizierungen_Vollmacht_Muster.pdf' download='Nostrifizierungen_Vollmacht_Muster.pdf' className='download-button'>
                                            <FaCloudDownloadAlt className='download-icon' />
                                            <span className='download-text'>Vorlage herunterladen</span>
                                        </a>
                                        {documentData && (
                                            <FileUpload
                                                tabindex={isViewOnly ? -1 : 0}
                                                id='vollmacht'
                                                name='vollmacht'
                                                title='Vollmacht'
                                                label=''
                                                description=''
                                                accept='.pdf,.png,.jpg'
                                                onDelete={(fileName) => handleDeleteFile(DOCUMENT_TYPES.VOLLMACHT, fileName)}
                                                onUploadSuccess={(fileName, guid) => handleDocumentUploadSuccess(DOCUMENT_TYPES.VOLLMACHT, fileName, guid)}
                                                value={getDocumentsByDescription(DOCUMENT_TYPES.VOLLMACHT)}
                                                antragId={antragId ?? 0}
                                                className={saveAttempted.start && (!getDocumentsByDescription(DOCUMENT_TYPES.VOLLMACHT) || getDocumentsByDescription(DOCUMENT_TYPES.VOLLMACHT).length === 0) ? 'error' : ''}
                                            />
                                        )}
                                        <p style={{ fontStyle: 'italic', fontSize: '0.9rem' }}>
                                            Bitte laden Sie hier die Vorlage für die Vollmacht herunter,
                                            füllen Sie diese aus und laden Sie das ausgefüllte Dokument anschließend hoch.
                                        </p>
                                    </>

                                )}
                            </div>
                            <div className='form-element'>
                                <p>Bitte wählen Sie aus, für welchen Bachelor-Studiengang Sie einen Antrag auf Nostrifizierung
                                    (akademische Anerkennung) stellen möchten.</p>
                                <select
                                    tabIndex={isViewOnly ? -1 : 0}
                                    className={`form-control form-select${saveAttempted.start && !applicationValidationData.bachDegree ? ' error' : ''}`}
                                    style={{ margin: "auto", maxWidth: "600px" }}
                                    title="Wählen Sie den Studiengang aus, für den Sie einen Antrag auf Nostrifizierung stellen möchten"
                                    name="bachDegree"
                                    value={applicationValidationData.bachDegree ?? ''}
                                    onChange={handleApplicationValidationDataChange}
                                >
                                    <option value="" disabled>Bitte auswählen</option>
                                    <option value="Gesundheits- und Krankenpflege">Gesundheits- und Krankenpflege</option>
                                    <option value="Physiotherapie">Physiotherapie</option>
                                    <option value="Ergotherapie">Ergotherapie</option>
                                    <option value="Hebammen">Hebammen</option>
                                    <option value="Musiktherapie">Musiktherapie</option>
                                </select>
                            </div>
                            <div className='form-element'>
                                <p>In welchem Land haben Sie Ihre Ausbildung absolviert?</p>
                                <select
                                    tabIndex={isViewOnly ? -1 : 0}
                                    className={`form-control form-select${saveAttempted.start && !applicationValidationData.educationCountry ? ' error' : ''}`}
                                    style={{ margin: "auto", maxWidth: "600px" }}
                                    title="Wählen Sie das Land aus, in dem Sie Ihre Ausbildung absolviert haben"
                                    name="educationCountry"
                                    value={applicationValidationData.educationCountry ?? ''}
                                    onChange={handleApplicationValidationDataChange}
                                >
                                    <option value="" disabled >Bitte auswählen</option>
                                    {countries.map((country, index) => (
                                        <option key={index} value={country}>
                                            {country}
                                        </option>
                                    ))}
                                </select>
                                {applicationValidationData.educationCountry && filteredCountries.includes(applicationValidationData.educationCountry) && applicationValidationData.bachDegree !== "Musiktherapie" && (
                                    <div>
                                        <p className='infotext'><b>Keine Nostrifizierung notwendig.</b> Sie erfüllen die Anforderungen,
                                            um einen Antrag auf Berufsanerkennung beim Bundesministerium für Gesundheit zu stellen.
                                            Für weitere Informationen klicken Sie {" "}
                                            <a
                                                href="https://www.sozialministerium.gv.at/Themen/Gesundheit/Medizin-und-Gesundheitsberufe/Anerkennung-(Berufe).html"
                                                target="_blank"
                                                rel="noopener noreferrer "
                                                style={{ color: "blue", textDecoration: "underline", fontWeight: "600" }}
                                            >
                                                hier.
                                            </a>
                                        </p>
                                    </div>
                                )}
                            </div>
                            {(
                                applicationValidationData.educationCountry !== null &&
                                (
                                    applicationValidationData.bachDegree === "Musiktherapie" ||
                                    !filteredCountries.includes(applicationValidationData.educationCountry)
                                )
                            ) &&
                                (<div className={`form-element${saveAttempted.start && applicationValidationData.hasJobRecognition === null ? ' error' : ''}`}>
                                    <div>
                                        <p>Verfügen Sie bereits über eine Berufsanerkennung in einem anderen EU/EWR-Land oder der Schweiz?</p>
                                        <label className='checkbox-label'>
                                            <input
                                                type="checkbox"
                                                name="hasJobRecognition"
                                                checked={applicationValidationData.hasJobRecognition === true}
                                                onChange={() => handleApplicationCheckboxChange("hasJobRecognition", true)}
                                            />
                                            Ja
                                            <span
                                                className='checkmark'
                                                tabIndex={isViewOnly ? -1 : 0}
                                                role="checkbox"
                                                aria-checked={applicationValidationData.hasJobRecognition === true}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleApplicationCheckboxChange("hasJobRecognition", true);
                                                    }
                                                }}
                                            ></span>
                                        </label>
                                        <label className='checkbox-label'>
                                            <input
                                                type="checkbox"
                                                name="hasJobRecognition"
                                                checked={applicationValidationData.hasJobRecognition === false}
                                                onChange={() => handleApplicationCheckboxChange("hasJobRecognition", false)}
                                            />
                                            Nein
                                            <span
                                                className='checkmark'
                                                tabIndex={isViewOnly ? -1 : 0}
                                                role="checkbox"
                                                aria-checked={applicationValidationData.hasJobRecognition === false}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleApplicationCheckboxChange("hasJobRecognition", false);
                                                    }
                                                }}
                                            ></span>
                                        </label>
                                    </div>
                                </div>
                                )}
                            {applicationValidationData.hasJobRecognition && (
                                <div className={`form-element${saveAttempted.start && applicationValidationData.hasJobExperience === null ? ' error' : ''}`}>
                                    <div>
                                        <p>Haben Sie im anerkannten Beruf bereits mindestens 3 Jahre in diesem Land gearbeitet?</p>
                                        <label className='checkbox-label'>
                                            <input
                                                type="checkbox"
                                                name="hasJobExperience"
                                                checked={applicationValidationData.hasJobExperience === true}
                                                onChange={() => handleApplicationCheckboxChange("hasJobExperience", true)}
                                            />
                                            Ja
                                            <span
                                                className='checkmark'
                                                tabIndex={isViewOnly ? -1 : 0}
                                                role="checkbox"
                                                aria-checked={applicationValidationData.hasJobExperience === true}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleApplicationCheckboxChange("hasJobExperience", true);
                                                    }
                                                }}
                                            ></span>
                                        </label>
                                        <label className='checkbox-label'>
                                            <input
                                                type="checkbox"
                                                name="hasJobExperience"
                                                checked={applicationValidationData.hasJobExperience === false}
                                                onChange={() => handleApplicationCheckboxChange("hasJobExperience", false)}
                                            />
                                            Nein
                                            <span
                                                className='checkmark'
                                                tabIndex={isViewOnly ? -1 : 0}
                                                role="checkbox"
                                                aria-checked={applicationValidationData.hasJobExperience === false}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleApplicationCheckboxChange("hasJobExperience", false);
                                                    }
                                                }}
                                            ></span>
                                        </label>
                                    </div>
                                    {applicationValidationData.hasJobExperience && applicationValidationData.bachDegree !== "Musiktherapie" && (
                                        <div>
                                            <p className='infotext'><b>Keine Nostrifizierung notwendig.</b> Sie erfüllen die Anforderungen,
                                                um einen Antrag auf Berufsanerkennung beim Bundesministerium für Gesundheit zu stellen.
                                                Für weitere Informationen klicken Sie {" "}
                                                <a
                                                    href="https://www.sozialministerium.gv.at/Themen/Gesundheit/Medizin-und-Gesundheitsberufe/Anerkennung-(Berufe).html"
                                                    target="_blank"
                                                    rel="noopener noreferrer "
                                                    style={{ color: "blue", textDecoration: "underline", fontWeight: "600" }}
                                                >
                                                    hier.
                                                </a>
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className={`form-element${saveAttempted.start && applicationValidationData.activeApplication === null ? ' error' : ''}`}>
                                <p>Haben Sie einen laufenden Antrag zur Nostrifizierung an einer <b>anderen</b> Hochschule in Österreich?</p>
                                <label className='checkbox-label'>
                                    <input
                                        type="checkbox"
                                        name="activeApplication"
                                        checked={applicationValidationData.activeApplication === true}
                                        onChange={() => handleApplicationCheckboxChange("activeApplication", true)}
                                    />
                                    Ja
                                    <span
                                        className='checkmark'
                                        tabIndex={isViewOnly ? -1 : 0}
                                        role="checkbox"
                                        aria-checked={applicationValidationData.activeApplication === true}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleApplicationCheckboxChange("activeApplication", true);
                                            }
                                        }}
                                    ></span>
                                </label>
                                <label className='checkbox-label'>
                                    <input
                                        type="checkbox"
                                        name="activeApplication"
                                        checked={applicationValidationData.activeApplication === false}
                                        onChange={() => handleApplicationCheckboxChange("activeApplication", false)}
                                    />
                                    Nein
                                    <span
                                        className='checkmark'
                                        tabIndex={isViewOnly ? -1 : 0}
                                        role="checkbox"
                                        aria-checked={applicationValidationData.activeApplication === false}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleApplicationCheckboxChange("activeApplication", false);
                                            }
                                        }}
                                    ></span>
                                </label>

                                {applicationValidationData.activeApplication && (
                                    <div>
                                        <p className='infotext'>Wir weisen darauf hin, dass es gemäß <b> §&nbsp;6 Abs 7 FHG </b> unzulässig ist, die
                                            Nostrifizierung gleichzeitig an zwei oder mehreren Hochschulen einzubringen.
                                        </p>
                                    </div>
                                )}
                            </div>
                            <div className={`form-element${saveAttempted.start && applicationValidationData.withdrawnApplication === null ? ' error' : ''}`}>
                                <p>Haben Sie bereits einen Antrag auf Nostrifizierung an einer <b>anderen</b> österreichischen Hochschule
                                    zurückgezogen?</p>
                                <label className='checkbox-label'>
                                    <input
                                        type="checkbox"
                                        name="withdrawnApplication"
                                        checked={applicationValidationData.withdrawnApplication === true}
                                        onChange={() => handleApplicationCheckboxChange("withdrawnApplication", true)}
                                    />
                                    Ja
                                    <span
                                        className='checkmark'
                                        tabIndex={isViewOnly ? -1 : 0}
                                        role="checkbox"
                                        aria-checked={applicationValidationData.withdrawnApplication === true}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleApplicationCheckboxChange("withdrawnApplication", true);
                                            }
                                        }}
                                    ></span>
                                </label>
                                <label className='checkbox-label'>
                                    <input
                                        type="checkbox"
                                        name="withdrawnApplication"
                                        checked={applicationValidationData.withdrawnApplication === false}
                                        onChange={() => handleApplicationCheckboxChange("withdrawnApplication", false)}
                                    />
                                    Nein
                                    <span
                                        className='checkmark'
                                        tabIndex={isViewOnly ? -1 : 0}
                                        role="checkbox"
                                        aria-checked={applicationValidationData.withdrawnApplication === false}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleApplicationCheckboxChange("withdrawnApplication", false);
                                            }
                                        }}
                                    ></span>
                                </label>

                                {applicationValidationData.withdrawnApplication && (
                                    <div>
                                        <p className='infotext'>Wir weisen darauf hin, dass es gemäß <b> §&nbsp;6 Abs 7 FHG </b> unzulässig ist,
                                            die Nostrifizierung nach Zurückziehung an einer anderen Hochschule einzubringen.
                                        </p>
                                    </div>
                                )}
                            </div>
                            <div className={`form-element${saveAttempted.start && applicationValidationData.educationUse === null ? ' error' : ''}`}>
                                <div className="education-use">
                                    <p>Der Antrag auf Nostrifizierung ist <b>zwingend</b> notwendig für: <br />
                                        (Wählen Sie eine der Optionen aus)</p>
                                    <button
                                        tabIndex={isViewOnly ? -1 : 0}
                                        type="button"
                                        name='educationUse'
                                        onClick={() => handleEducationUse("Job")}
                                        style={{
                                            backgroundColor: applicationValidationData.educationUse === "Job" ? "#757d85" : "#e0e0e0",
                                            color: applicationValidationData.educationUse === "Job" ? "#fff" : "#000",
                                        }}>
                                        Die angestrebte Berufsausübung als diplomierte*r Gesundheits- und Krankenpfleger*in, Physiotherapeut*in, Ergotherapeut*in, Hebamme oder Musiktherapeut*in
                                    </button>

                                    <button
                                        tabIndex={isViewOnly ? -1 : 0}
                                        type="button"
                                        name='educationUse'
                                        onClick={() => handleEducationUse("Studium")}
                                        style={{
                                            backgroundColor: applicationValidationData.educationUse === "Studium" ? "#757d85" : "#e0e0e0",
                                            color: applicationValidationData.educationUse === "Studium" ? "#fff" : "#000",
                                        }}>
                                        Ein weiterführendes Masterstudium in Österreich
                                    </button>
                                </div>
                            </div>
                            <p className='antrag-subheading' style={{ marginTop: '40px' }}>
                                Aufgrund bestehender Förderrichtlinien bitten wir Sie, die folgenden Fragen zu beantworten:
                            </p>
                            <div className={`form-element${saveAttempted.start && applicationValidationData.workInLowerAustria === null ? ' error' : ''}`}>
                                <p>Sind oder waren Sie in Niederösterreich beruflich tätig?</p>
                                <label className='checkbox-label'>
                                    <input
                                        type="checkbox"
                                        name="workInLowerAustria"
                                        checked={applicationValidationData.workInLowerAustria === true}
                                        onChange={() => handleApplicationCheckboxChange("workInLowerAustria", true)}
                                    />
                                    Ja
                                    <span
                                        className='checkmark'
                                        tabIndex={isViewOnly ? -1 : 0}
                                        role="checkbox"
                                        aria-checked={applicationValidationData.workInLowerAustria === true}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleApplicationCheckboxChange("workInLowerAustria", true);
                                            }
                                        }}
                                    ></span>
                                </label>
                                <label className='checkbox-label'>
                                    <input
                                        type="checkbox"
                                        name="workInLowerAustria"
                                        checked={applicationValidationData.workInLowerAustria === false}
                                        onChange={() => handleApplicationCheckboxChange("workInLowerAustria", false)}
                                    />
                                    Nein
                                    <span
                                        className='checkmark'
                                        tabIndex={isViewOnly ? -1 : 0}
                                        role="checkbox"
                                        aria-checked={applicationValidationData.workInLowerAustria === false}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleApplicationCheckboxChange("workInLowerAustria", false);
                                            }
                                        }}
                                    ></span>
                                </label>
                            </div>
                            <div className={`form-element${saveAttempted.start && applicationValidationData.plannedWorkInLowerAustria === null ? ' error' : ''}`}>
                                <p>Planen Sie in Niederösterreich beruflich tätig zu werden?</p>
                                <label className='checkbox-label'>
                                    <input
                                        type="checkbox"
                                        name="plannedWorkInLowerAustria"
                                        checked={applicationValidationData.plannedWorkInLowerAustria === true}
                                        onChange={() => handleApplicationCheckboxChange("plannedWorkInLowerAustria", true)}
                                    />
                                    Ja
                                    <span
                                        className='checkmark'
                                        tabIndex={isViewOnly ? -1 : 0}
                                        role="checkbox"
                                        aria-checked={applicationValidationData.plannedWorkInLowerAustria === true}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleApplicationCheckboxChange("plannedWorkInLowerAustria", true);
                                            }
                                        }}
                                    ></span>
                                </label>
                                <label className='checkbox-label'>
                                    <input
                                        type="checkbox"
                                        name="plannedWorkInLowerAustria"
                                        checked={applicationValidationData.plannedWorkInLowerAustria === false}
                                        onChange={() => handleApplicationCheckboxChange("plannedWorkInLowerAustria", false)}
                                    />
                                    Nein
                                    <span
                                        className='checkmark'
                                        tabIndex={isViewOnly ? -1 : 0}
                                        role="checkbox"
                                        aria-checked={applicationValidationData.plannedWorkInLowerAustria === false}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleApplicationCheckboxChange("plannedWorkInLowerAustria", false);
                                            }
                                        }}
                                    ></span>
                                </label>
                            </div>
                            <div className={`form-element${saveAttempted.start && applicationValidationData.livesInLowerAustria === null ? ' error' : ''}`}>
                                <p>Haben oder hatten Sie einen Wohnsitz in Niederösterreich?</p>
                                <label className='checkbox-label'>
                                    <input
                                        type="checkbox"
                                        name="livesInLowerAustria"
                                        checked={applicationValidationData.livesInLowerAustria === true}
                                        onChange={() => handleApplicationCheckboxChange("livesInLowerAustria", true)}
                                    />
                                    Ja
                                    <span
                                        className='checkmark'
                                        tabIndex={isViewOnly ? -1 : 0}
                                        role="checkbox"
                                        aria-checked={applicationValidationData.livesInLowerAustria === true}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleApplicationCheckboxChange("livesInLowerAustria", true);
                                            }
                                        }}
                                    ></span>
                                </label>
                                <label className='checkbox-label'>
                                    <input
                                        type="checkbox"
                                        name="livesInLowerAustria"
                                        checked={applicationValidationData.livesInLowerAustria === false}
                                        onChange={() => handleApplicationCheckboxChange("livesInLowerAustria", false)}
                                    />
                                    Nein
                                    <span
                                        className='checkmark'
                                        tabIndex={isViewOnly ? -1 : 0}
                                        role="checkbox"
                                        aria-checked={applicationValidationData.livesInLowerAustria === false}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleApplicationCheckboxChange("livesInLowerAustria", false);
                                            }
                                        }}
                                    ></span>
                                </label>
                            </div>

                            <AntragButtons
                                onBack={() => {
                                    window.history.back();
                                }}
                                onNext={() => {
                                    setActiveSection('Persönliche Daten');
                                    scrollToPosition();
                                }}
                                onSave={() => {
                                    handleSave(true);
                                    setSaveAttempted(prev => ({ ...prev, start: true }));
                                }}
                                isNextDisabled={!isApplicationValidationDataValid()}
                                loading={loading}
                                isViewOnly={location.state?.viewAntrag}
                                isFirstPage={true}
                            />
                        </div>
                    )}
                    {activeSection === 'Persönliche Daten' && (
                        <div>
                            <h2 className="antrag-heading">Persönliche Daten Antragsteller*in</h2>
                            <PersonalDataForm
                                formData={personalData}
                                handleChange={handlePersonalDataChange}
                                countries={countries}
                                isViewOnly={isViewOnly}
                                showValidation={saveAttempted.personal}
                            />
                            <AntragButtons
                                onBack={() => {
                                    setActiveSection('Antrag starten');
                                    scrollToPosition();
                                }}
                                onNext={() => {
                                    setActiveSection('Ausbildung');
                                    scrollToPosition();
                                }}
                                onSave={() => {
                                    handleSave(true);
                                    setSaveAttempted(prev => ({ ...prev, personal: true }));
                                }}
                                isNextDisabled={!isPersonalDataValid()}
                                loading={loading}
                                isViewOnly={location.state?.viewAntrag}
                            />
                        </div>
                    )}
                    {activeSection === 'Ausbildung' && (
                        <div>
                            <h2 className="antrag-heading">Ausbildung</h2>
                            <div className='antrag-ausbildung-header'>
                                Schulabschluss
                            </div>
                            <div className='antrag-ausbildung'>
                                <div>
                                    <h5>Details zur Schulbildung</h5>
                                    <p>Bitte tragen Sie die folgenden Informationen zu der Schule ein,
                                        an der Sie Ihre Matura, Reifeprüfung bzw. Prüfung, die zum Hochschulzugang
                                        berechtigt, abgeschlossen haben.</p>
                                </div>
                                <div>
                                    <div className='form-group-ausbildung'>
                                        <label className='form-label' htmlFor='schoolName'>
                                            Name der Schule
                                        </label>
                                        <input
                                            id='schoolName'
                                            tabIndex={isViewOnly ? -1 : 0}
                                            className={`${saveAttempted.education && !schoolData.schoolName ? ' error' : ''}`}
                                            type='text'
                                            name='schoolName'
                                            maxLength={50}
                                            value={schoolData.schoolName ?? ''}
                                            onChange={handleSchoolDataChange} />
                                    </div>
                                    <div className='form-group-ausbildung'>
                                        <label className='form-label' htmlFor='schoolStreet'>
                                            Straße
                                        </label>
                                        <input
                                            id='schoolStreet'
                                            tabIndex={isViewOnly ? -1 : 0}
                                            className={`${saveAttempted.education && !schoolData.schoolStreet ? ' error' : ''}`}
                                            type='text'
                                            name='schoolStreet'
                                            maxLength={40}
                                            value={schoolData.schoolStreet ?? ''}
                                            onChange={handleSchoolDataChange} />
                                    </div>
                                    <div className='form-group-ausbildung'>
                                        <label className='form-label' htmlFor='schoolStreetNumber'>
                                            Hausnummer
                                        </label>
                                        <input
                                            id='schoolStreetNumber'
                                            tabIndex={isViewOnly ? -1 : 0}
                                            className={`${saveAttempted.education && !schoolData.schoolStreetNumber ? ' error' : ''}`}
                                            type='tel'
                                            name='schoolStreetNumber'
                                            maxLength={8}
                                            value={schoolData.schoolStreetNumber ?? ''}
                                            onChange={handleSchoolDataChange} />
                                    </div>
                                </div>
                                <div>
                                    <div className='form-group-ausbildung'>
                                        <label className='form-label' htmlFor='schoolZip'>
                                            Postleitzahl
                                        </label>
                                        <input
                                            id='schoolZip'
                                            tabIndex={isViewOnly ? -1 : 0}
                                            className={`${saveAttempted.education && !schoolData.schoolZip ? ' error' : ''}`}
                                            type='tel'
                                            name='schoolZip'
                                            maxLength={10}
                                            value={schoolData.schoolZip ?? ''}
                                            onChange={handleSchoolDataChange} />

                                    </div>
                                    <div className='form-group-ausbildung'>
                                        <label className='form-label' htmlFor='schoolCity'>
                                            Ort
                                        </label>
                                        <input
                                            id='schoolCity'
                                            tabIndex={isViewOnly ? -1 : 0}
                                            className={`${saveAttempted.education && !schoolData.schoolCity ? ' error' : ''}`}
                                            type='text'
                                            name='schoolCity'
                                            maxLength={50}
                                            value={schoolData.schoolCity ?? ''}
                                            onChange={handleSchoolDataChange} />
                                    </div>
                                    <div className='form-group-ausbildung'>
                                        <label className='form-label' htmlFor='schoolCountry'>
                                            Land
                                        </label>
                                        <select
                                            id='schoolCountry'
                                            tabIndex={isViewOnly ? -1 : 0}
                                            className={`form-control form-select${saveAttempted.education && !schoolData.schoolCountry ? ' error' : ''}`}
                                            style={{ height: '46px', border: '1px solid #cccccc' }}
                                            title='Wählen Sie das Land aus, in dem sich die Schule befindet'
                                            name='schoolCountry'
                                            value={schoolData.schoolCountry ?? ''}
                                            onChange={handleSchoolDataChange}
                                        >
                                            <option value="" disabled>Bitte auswählen</option>
                                            {countries.map((country, index) => (
                                                <option key={index} value={country}>
                                                    {country}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            {universityData.map((data, index) => (
                                <EducationDataForm
                                    key={index}
                                    formData={data}
                                    handleChange={(e) => handleEducationDataChange(index, e)}
                                    handleHasThesisChange={(value) => handleThesisChange(index, value)}
                                    countries={countries}
                                    isViewOnly={isViewOnly}
                                    showValidation={saveAttempted.education}
                                />
                            ))}
                            <div className='antrag-ausbildung'>
                                <div />
                                <div className='degree-buttons'>
                                    {universityData.length > 1 && (
                                        <button
                                            tabIndex={isViewOnly ? -1 : 0}
                                            className='add-degree-button'
                                            type='button'
                                            onClick={removeEducationData}>
                                            <IoIosRemove />  Studienabschluss entfernen
                                        </button>
                                    )}
                                    {universityData.length < 4 && (
                                        <button
                                            tabIndex={isViewOnly ? -1 : 0}
                                            className='add-degree-button'
                                            type='button'
                                            onClick={addEducationData}>
                                            <IoIosAdd /> Studienabschluss hinzufügen
                                        </button>
                                    )}
                                </div>
                            </div>
                            <AntragButtons
                                onBack={() => {
                                    setActiveSection('Persönliche Daten');
                                    scrollToPosition();
                                }}
                                onNext={() => {
                                    setActiveSection('Berufserfahrung');
                                    scrollToPosition();
                                }}
                                onSave={() => {
                                    handleSave(true);
                                    setSaveAttempted(prev => ({ ...prev, education: true }));
                                }}
                                isNextDisabled={!isEducationDataValid()}
                                loading={loading}
                                isViewOnly={location.state?.viewAntrag}
                            />
                        </div>
                    )}

                    {activeSection === 'Berufserfahrung' && (
                        <div>
                            <h2 className="antrag-heading">Berufsberechtigung und Berufserfahrung</h2>
                            <div className='berufserfahrung-section'>
                                <div className='berufserfahrung-validation'>
                                    <p className='berufserfahrung-validation-paragrapgh'>
                                        Gibt es in Ihrem Herkunftsland ein/e Gesundheitsberuferegister/Registration Certificate/Berufslizenz/etc.?
                                    </p>
                                    <div className={`${saveAttempted.work && workValidationData.hasExam === null ? ' error' : ''}`}>
                                        <label className='checkbox-label'>
                                            <input
                                                type="checkbox"
                                                name="hasExam"
                                                checked={workValidationData.hasExam === true}
                                                onChange={() => handleWorkValidationChange("hasExam", true)}
                                            />
                                            Ja
                                            <span
                                                tabIndex={isViewOnly ? -1 : 0}
                                                className="checkmark"
                                                role="checkbox"
                                                aria-checked={workValidationData.hasExam === true}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleWorkValidationChange('hasExam', true);
                                                    }
                                                }}
                                            > </span>
                                        </label>
                                        <label className='checkbox-label'>
                                            <input
                                                type="checkbox"
                                                name="hasExam"
                                                checked={workValidationData.hasExam === false}
                                                onChange={() => handleWorkValidationChange("hasExam", false)}
                                            />
                                            Nein
                                            <span
                                                tabIndex={isViewOnly ? -1 : 0}
                                                className="checkmark"
                                                role="checkbox"
                                                aria-checked={workValidationData.hasExam === false}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleWorkValidationChange('hasExam', false);
                                                    }
                                                }}
                                            > </span>
                                        </label>
                                    </div>
                                </div>
                                {workValidationData.hasExam && (
                                    <div className='berufserfahrung-validation'>
                                        <p className='berufserfahrung-validation-paragrapgh'>
                                            Verfügen Sie in Ihrem Herkunftsland über eine offizielle Berufsberechtigung oder sind Sie im Gesundheitsberuferegister eingetragen?
                                        </p>
                                        <div className={`${saveAttempted.work && workValidationData.hasPassedExam === null ? 'error' : ''}`}>
                                            <label className='checkbox-label'>
                                                <input
                                                    tabIndex={isViewOnly ? -1 : 0}
                                                    type="checkbox"
                                                    name="hasPassedExam"
                                                    checked={workValidationData.hasPassedExam === true}
                                                    onChange={() => handleWorkValidationChange("hasPassedExam", true)}
                                                />
                                                Ja
                                                <span
                                                    tabIndex={isViewOnly ? -1 : 0}
                                                    className="checkmark"
                                                    role="checkbox"
                                                    aria-checked={workValidationData.hasPassedExam === true}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            handleWorkValidationChange('hasPassedExam', true);
                                                        }
                                                    }}
                                                > </span>
                                            </label>
                                            <label className='checkbox-label'>
                                                <input
                                                    tabIndex={isViewOnly ? -1 : 0}
                                                    type="checkbox"
                                                    name="hasPassedExam"
                                                    checked={workValidationData.hasPassedExam === false}
                                                    onChange={() => handleWorkValidationChange("hasPassedExam", false)}
                                                />
                                                Nein
                                                <span
                                                    tabIndex={isViewOnly ? -1 : 0}
                                                    className="checkmark"
                                                    role="checkbox"
                                                    aria-checked={workValidationData.hasPassedExam === false}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            handleWorkValidationChange('hasPassedExam', false);
                                                        }
                                                    }}
                                                > </span>
                                            </label>
                                        </div>
                                        {(workValidationData.hasExam && workValidationData.hasPassedExam === false) && (
                                            <div className="no-exam-reason">
                                                <p style={{ textAlign: "left" }}>Bitte erklären Sie, wieso Sie die Berufsberechtigung/Eintragung nicht haben.</p>
                                                <textarea
                                                    tabIndex={isViewOnly ? -1 : 0}
                                                    className={`input-field ${saveAttempted.work && !workValidationData.hasNoExamReason ? ' error' : ''}`}
                                                    name='hasNoExamReason'
                                                    maxLength={200}
                                                    placeholder='Bitte geben Sie einen Grund an'
                                                    rows={5}
                                                    value={workValidationData.hasNoExamReason ?? ''}
                                                    onChange={(e) => setWorkValidationData(prevState => ({
                                                        ...prevState,
                                                        hasNoExamReason: e.target.value
                                                    })
                                                    )}>
                                                </textarea>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className='berufserfahrung-validation'>
                                    <p className='berufserfahrung-validation-paragrapgh'>
                                        Verfügen Sie über Berufserfahrung (ausgenommen Praktika, die im Rahmen des Studiums absolviert wurden)?
                                    </p>
                                    <div className={`${saveAttempted.work && workValidationData.hasWorkExperience === null ? 'error' : ''}`}>
                                        <label className='checkbox-label'>
                                            <input
                                                type="checkbox"
                                                name="hasWorkExperience"
                                                checked={workValidationData.hasWorkExperience === true}
                                                onChange={() => handleWorkValidationChange("hasWorkExperience", true)}
                                            />
                                            Ja
                                            <span
                                                tabIndex={isViewOnly ? -1 : 0}
                                                className="checkmark"
                                                role="checkbox"
                                                aria-checked={workValidationData.hasWorkExperience === true}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleWorkValidationChange('hasWorkExperience', true);
                                                    }
                                                }}
                                            > </span>
                                        </label>
                                        <label className='checkbox-label'>
                                            <input
                                                type="checkbox"
                                                name="hasWorkExperience"
                                                checked={workValidationData.hasWorkExperience === false}
                                                onChange={() => handleWorkValidationChange("hasWorkExperience", false)}
                                            />
                                            Nein
                                            <span
                                                tabIndex={isViewOnly ? -1 : 0}
                                                className="checkmark"
                                                role="checkbox"
                                                aria-checked={workValidationData.hasWorkExperience === false}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleWorkValidationChange('hasWorkExperience', false);
                                                    }
                                                }}
                                            > </span>
                                        </label>
                                    </div>
                                </div>

                                {workValidationData.hasWorkExperience && (
                                    <>
                                        <div className='antrag-ausbildung-header'>
                                            Berufserfahrung
                                        </div>
                                        {workExperienceData.map((data, index) => (
                                            <WorkExperienceDataForm
                                                key={index}
                                                index={index}
                                                formData={data}
                                                countries={countries}
                                                handleChange={(e) => handleWorkExperienceChange(index, e)}
                                                isViewOnly={isViewOnly}
                                                showValidation={saveAttempted.work}
                                            />
                                        ))}
                                        <div className='degree-buttons'>
                                            {workExperienceData.length > 1 && (
                                                <button
                                                    tabIndex={isViewOnly ? -1 : 0}
                                                    className='add-degree-button'
                                                    type='button'
                                                    onClick={removeWorkExperience}>
                                                    <IoIosRemove />  Berufserfahrung entfernen
                                                </button>
                                            )}
                                            {workExperienceData.length < 8 && (
                                                <button
                                                    tabIndex={isViewOnly ? -1 : 0}
                                                    className='add-degree-button'
                                                    type='button'
                                                    onClick={addWorkExperience}>
                                                    <IoIosAdd /> Berufserfahrung hinzufügen
                                                </button>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                            <AntragButtons
                                onBack={() => {
                                    setActiveSection('Ausbildung');
                                    scrollToPosition();
                                }}
                                onNext={() => {
                                    setActiveSection('Dokumente');
                                    scrollToPosition();
                                }}
                                onSave={() => {
                                    setSaveAttempted(prev => ({ ...prev, work: true }));
                                    handleSave(true);
                                }}
                                isNextDisabled={!isWorkValidationDataValid()}
                                loading={loading}
                                isViewOnly={location.state?.viewAntrag}
                            />
                        </div>
                    )}

                    {activeSection === 'Dokumente' && (
                        <>
                            <h2 className="antrag-heading">Dokumente</h2>
                            <DocumentForm
                                familyStatus={personalData.familyStatus}
                                hasThesis={universityData.some(edu => edu.hasThesis === true)}
                                hasWorkExperience={workValidationData.hasWorkExperience}
                                hasExam={workValidationData.hasExam}
                                bachDegree={applicationValidationData.bachDegree}
                                formData={documentData}
                                getDocumentsByDescription={getDocumentsByDescription}
                                handleDelete={handleDeleteFile}
                                onUploadSuccess={handleDocumentUploadSuccess}
                                otherDocumentData={otherDocumentData}
                                handleOtherDocumentChange={handleOtherDocumentDataChange}
                                handleDeleteOther={handleDeleteOther}
                                addOtherDocument={addOtherDocument}
                                handleOtherDocumentDelete={deleteOtherDocument}
                                handleOtherDocumentUploadSuccess={handleOtherDocumentUploadSuccess}
                                antragId={antragId ?? 0}
                                isViewOnly={location.state?.viewAntrag}
                                showValidation={saveAttempted.documents}
                            />
                            <AntragButtons
                                onBack={() => {
                                    setActiveSection('Berufserfahrung');
                                    scrollToPosition();
                                }}
                                onNext={() => {
                                    setActiveSection('Abschluss');
                                    scrollToPosition();
                                }}
                                onSave={() => {
                                    setSaveAttempted(prev => ({ ...prev, documents: true }));
                                    handleSave(true);
                                }}
                                isNextDisabled={!isDocumentDataValid()}
                                loading={loading}
                                isViewOnly={location.state?.viewAntrag}
                            />
                        </>
                    )}
                    {activeSection === 'Abschluss' && (
                        <div>
                            <h4 className='antrag-heading'>Datenschutz</h4>
                            <div className='switch-container'>
                                <label className="switch" htmlFor='dataProcessing'>
                                    <input tabIndex={-1} id='dataProcessing' type="checkbox" checked={dataProtectionData.dataProcessing} onChange={() => toggleSwitch('dataProcessing')} />
                                    <span
                                        tabIndex={isViewOnly ? -1 : 0}
                                        className="slider"
                                        aria-checked={dataProtectionData.dataProcessing}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                toggleSwitch('dataProcessing');
                                            }
                                        }}
                                    > </span>
                                </label>
                                <div>
                                    <p className='switch-paragraph'>
                                        Die Verarbeitung der hier bekannt gegebenen Daten erfolgt
                                        zur Abwicklung meines Ansuchens gemäß Artikel 6 Abs. 1 lit. b DSGVO.
                                        Die Details zur Verarbeitung von personenbezogenen Daten sind der
                                        Datenschutzerklärung des IMC Krems unter {' '}
                                        <a tabIndex={-1} href='https://www.imc.ac.at/datenschutzerklaerung/' target="_blank" rel='noreferrer noopener' style={{ color: 'blue' }}>
                                            Datenschutzerklärung - IMC Krems</a> zu entnehmen.</p>
                                </div>
                                <label className="switch" htmlFor='correctData'>
                                    <input tabIndex={-1} id='correctData' type="checkbox" checked={dataProtectionData.correctData} onChange={() => toggleSwitch('correctData')} />
                                    <span
                                        tabIndex={isViewOnly ? -1 : 0}
                                        className="slider"
                                        aria-checked={dataProtectionData.correctData}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                toggleSwitch('correctData');
                                            }
                                        }}
                                    > </span>
                                </label>
                                <div>
                                    <p className='switch-paragraph'>
                                        Hiermit bestätige ich, dass ich alle Angaben meines Antrages auf
                                        Nostrifizierung wahrheitsgemäß und vollständig gemacht, sowie
                                        alle geforderten Nachweise beigelegt habe. Die Frist läuft ab dem vom IMC Krems
                                        bestätigten Vorliegen <b>aller</b> notwendigen Unterlagen.
                                    </p>
                                </div>
                                <label className="switch" htmlFor='oath'>
                                    <input tabIndex={-1} id='oath' type="checkbox" checked={dataProtectionData.oath} onChange={() => toggleSwitch('oath')} />
                                    <span
                                        tabIndex={isViewOnly ? -1 : 0}
                                        className="slider"
                                        aria-checked={dataProtectionData.oath}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                toggleSwitch('oath');
                                            }
                                        }}
                                    > </span>
                                </label>
                                <div>
                                    <p className='switch-paragraph'>
                                        Ich erkläre an Eides statt, dass ich an keiner österreichischen Behörde, Institution oder
                                        Bildungseinrichtung einen Nostrifizierungsantrag gleichen Inhaltes eingereicht oder zurückgezogen
                                        habe, da dies gemäß <b> §&nbsp;6 (7) FHG </b> unzulässig ist. Zum Zwecke der Prüfung
                                        dieser Angabe behält sich das IMC Krems einen entstprechenden (Daten-)
                                        Austausch mit den Behörden und anderen Institutionen/Bildungseinrichtungen vor.
                                    </p>
                                </div>
                                <label className="switch" htmlFor='feeExpires' style={{ marginTop: 2 }}>
                                    <input tabIndex={-1} id='feeExpires' type="checkbox" checked={dataProtectionData.feeExpires} onChange={() => toggleSwitch('feeExpires')} />
                                    <span
                                        tabIndex={isViewOnly ? -1 : 0}
                                        className="slider"
                                        aria-checked={dataProtectionData.feeExpires}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                toggleSwitch('feeExpires');
                                            }
                                        }}
                                    > </span>
                                </label>
                                <div>
                                    <p className='switch-paragraph'>
                                        Ich nehme zur Kenntniss, dass die Nostrifizierungstaxe verfällt, wenn der Antrag auf Nostrifizierung abgewiesen oder
                                        zurückgezogen wird.
                                    </p>
                                </div>
                            </div>
                            <div>
                                {canSubmit ? (
                                    <>
                                        {location.state?.viewAntrag && (location.state?.status === 'Eingereicht' || location.state?.status === 'Abgeschlossen') ?
                                            (
                                                <>
                                                    <p style={{ padding: '10px', textAlign: 'left' }}>
                                                        Dieser Antrag wurde bereits eingereicht. <br />
                                                        Nach erfolgreicher Prüfung wird der Status Ihres Antrags aktualisiert.
                                                    </p>
                                                </>
                                            ) : (
                                                <>
                                                    <h4 className='antrag-heading' style={{ marginTop: "20px", marginBottom: "10px" }}>Abschluss</h4>
                                                    <button disabled={!Object.values(dataProtectionData).every(Boolean)} className='antrag-payment-button'
                                                        onClick={handleAgenySubmit}>
                                                        Antrag einreichen <IoSend />
                                                    </button>
                                                </>
                                            )}
                                    </>
                                ) : (
                                    <div className='payment-container'>
                                        <h4 className='antrag-heading' style={{ marginTop: "20px", marginBottom: "10px" }}>Bezahlung</h4>
                                        {location.state?.viewAntrag && (location.state?.status === 'Eingereicht' || location.state?.status === 'Abgeschlossen') ?
                                            (<div>
                                                <p style={{ padding: '10px', textAlign: 'left' }}>
                                                    Die Zahlung für diesen Antrag ist bereits erfolgt und der Antrag wurde eingereicht. <br />
                                                    Nach erfolgreicher Prüfung wird der Status Ihres Antrags aktualisiert und Sie können die Rechnung am Dashboard herunterladen.
                                                </p>
                                            </div>)
                                            : (
                                                <button disabled={!Object.values(dataProtectionData).every(Boolean)} className='antrag-payment-button'
                                                    onClick={handlePayment}>
                                                    Jetzt bezahlen <IoIosCard />
                                                </button>
                                            )}
                                    </div>
                                )}
                            </div>


                            <div className='antrag-buttons'>
                                <button type='button' className='antrag-switch-page'
                                    onClick={() => {
                                        setActiveSection('Dokumente');
                                        scrollToPosition();
                                    }}>
                                    <FaArrowLeft className='antrag-switch-page-icon' /> Zurück
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div >

    )
}

export default Antrag